import React from 'react';

import { faPlay } from '@fortawesome/pro-solid-svg-icons/faPlay';
import { faRotateRight } from '@fortawesome/pro-solid-svg-icons/faRotateRight';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import * as css from './PostVideo.module.css';

export interface PostVideoProps {
  src: string;
  /** First frame, shown until playback starts and used as the share image fallback. */
  poster: string;
  /** Accessible name for the video and the overlay buttons. */
  label: string;
}

type Phase = 'idle' | 'playing' | 'ended';

/**
 * Silent, inline demo clip. Shows the poster behind a Play button until the
 * reader taps it, then offers a Replay when it ends. Never autoplays.
 */
export const PostVideo: React.FC<PostVideoProps> = ({ src, poster, label }) => {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [phase, setPhase] = React.useState<Phase>('idle');

  const play = React.useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = 0;
    void video.play().then(
      () => setPhase('playing'),
      () => setPhase('idle')
    );
  }, []);

  return (
    <div className={css.frame}>
      <video
        ref={videoRef}
        className={css.video}
        src={src}
        poster={poster}
        muted
        playsInline
        preload="metadata"
        aria-label={label}
        onEnded={() => setPhase('ended')}
        onPlay={() => setPhase('playing')}
      />
      {phase !== 'playing' && (
        <button
          type="button"
          className={css.overlay}
          onClick={play}
          aria-label={phase === 'ended' ? `Replay ${label}` : `Play ${label}`}
        >
          <span className={css.overlayButton}>
            <FontAwesomeIcon icon={phase === 'ended' ? faRotateRight : faPlay} />
            <span>{phase === 'ended' ? 'Replay' : 'Play'}</span>
          </span>
        </button>
      )}
    </div>
  );
};
